import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || null);
  const [translations, setTranslations] = useState({});
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load translations when language changes
  useEffect(() => {
    if (language) {
      localStorage.setItem('language', language);
      loadTranslations(language);
    }
  }, [language]);

  // Load user data if token exists
  useEffect(() => {
    if (token) {
      loadUser();
    }
  }, [token]);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const loadTranslations = async (lang) => {
    try {
      const response = await axios.get(`${API}/translations/${lang}`);
      setTranslations(response.data);
    } catch (error) {
      console.error('Error loading translations:', error);
    }
  };

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Error loading user:', error);
      logout();
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setFavorites(userData.favorites || []);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setFavorites([]);
  };

  const t = (key) => {
    return translations[key] || key;
  };

  const addToCart = (product, sizeMl, quantity = 1) => {
    setCart(prev => {
      const existingIndex = prev.findIndex(
        item => item.product_id === product.id && item.size_ml === sizeMl
      );
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }
      
      return [...prev, {
        product_id: product.id,
        product,
        size_ml: sizeMl,
        quantity
      }];
    });
  };

  const removeFromCart = (productId, sizeMl) => {
    setCart(prev => prev.filter(
      item => !(item.product_id === productId && item.size_ml === sizeMl)
    ));
  };

  const updateCartQuantity = (productId, sizeMl, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, sizeMl);
      return;
    }
    
    setCart(prev => prev.map(item => {
      if (item.product_id === productId && item.size_ml === sizeMl) {
        return { ...item, quantity };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCart([]);
  };

  const toggleFavorite = async (productId) => {
    if (!token) return false;
    
    const isFavorite = favorites.includes(productId);
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => prev.filter(id => id !== productId));
      } else {
        await axios.post(`${API}/favorites/${productId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => [...prev, productId]);
      }
      return true;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return false;
    }
  };

  const isFavorite = (productId) => {
    return favorites.includes(productId);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const size = item.product?.sizes?.find(s => s.ml === item.size_ml);
      const price = size?.price || item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    language,
    setLanguage,
    translations,
    t,
    user,
    token,
    login,
    register,
    logout,
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    favorites,
    toggleFavorite,
    isFavorite,
    isLoading,
    setIsLoading,
    API
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
